const COPY_PASTE_MAP_ATTRIBUTE = 'CopyPastable Map link';
const SUGGESTED_CHECKOUT_ADDRESS_ATTRIBUTE = 'Suggested checkout address';
const WAYFINDER_MAP_ATTRIBUTE = 'Link to Map';
const LATITUDE_ATTRIBUTE = 'Latitude';
const LONGITUDE_ATTRIBUTE = 'Longitude';
const FULFILLMENT_INTENT_ATTRIBUTE = 'Wake Stop fulfillment intent';
const RENTAL_STOCKUP_INTENT = 'Rental boat stock-up';

let syncInFlight = false;
let lastSyncedMapLink = '';
let locationHelpNode = null;

function selectedFulfillmentIntent() {
  const checked = document.querySelector(`input[name="attributes[${FULFILLMENT_INTENT_ATTRIBUTE}]"]:checked`);
  return checked?.value || '';
}

function isRentalStockupSelected(attributes = {}) {
  return selectedFulfillmentIntent() === RENTAL_STOCKUP_INTENT ||
    attributes[FULFILLMENT_INTENT_ATTRIBUTE] === RENTAL_STOCKUP_INTENT;
}

function cleanCoordinate(value) {
  return String(value || '').trim().replace(/\s+/g, '');
}

function mapLinkFromCoordinates(latitude, longitude) {
  const lat = cleanCoordinate(latitude);
  const lng = cleanCoordinate(longitude);

  if (!lat || !lng) {
    return '';
  }

  return `https://maps.google.com/?q=${lat},${lng}`;
}

function mapLinkFromWayfinderUrl(url) {
  try {
    const parsed = new URL(url);
    const query = parsed.searchParams.get('query') || parsed.searchParams.get('q');

    if (!query) {
      return '';
    }

    const [latitude, longitude] = query.split(',');
    return mapLinkFromCoordinates(latitude, longitude);
  } catch (error) {
    return '';
  }
}

function buildCopyPasteMapLink(attributes) {
  const fromCoordinates = mapLinkFromCoordinates(
    attributes[LATITUDE_ATTRIBUTE],
    attributes[LONGITUDE_ATTRIBUTE]
  );

  if (fromCoordinates) {
    return fromCoordinates;
  }

  return mapLinkFromWayfinderUrl(attributes[WAYFINDER_MAP_ATTRIBUTE]);
}

function buildSuggestedCheckoutAddress(attributes) {
  const lat = cleanCoordinate(attributes[LATITUDE_ATTRIBUTE]);
  const lng = cleanCoordinate(attributes[LONGITUDE_ATTRIBUTE]);

  if (!lat || !lng) {
    return '';
  }

  return `GPS: ${lat}, ${lng}; Austin, TX 78734; US`;
}

function hasSavedCoordinates(attributes) {
  return Boolean(
    cleanCoordinate(attributes[LATITUDE_ATTRIBUTE]) &&
      cleanCoordinate(attributes[LONGITUDE_ATTRIBUTE])
  );
}

function ensureLocationHelpNode() {
  if (locationHelpNode) {
    return locationHelpNode;
  }

  const wayfinderContainer = document.querySelector('[data-wakestop-wayfinder-container]');
  if (!wayfinderContainer) {
    return null;
  }

  locationHelpNode = document.createElement('p');
  locationHelpNode.className = 'wakestop-wayfinder-location-help';
  locationHelpNode.hidden = true;
  locationHelpNode.setAttribute('role', 'status');
  wayfinderContainer.insertAdjacentElement('afterend', locationHelpNode);
  return locationHelpNode;
}

function showLocationHelp(message) {
  if (isRentalStockupSelected()) {
    hideLocationHelp();
    return;
  }

  const node = ensureLocationHelpNode();
  if (!node) {
    return;
  }

  node.textContent = message;
  node.hidden = false;
}

function hideLocationHelp() {
  if (!locationHelpNode) {
    return;
  }

  locationHelpNode.hidden = true;
}

async function readCartAttributes() {
  const cartResponse = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });

  if (!cartResponse.ok) {
    return null;
  }

  const cart = await cartResponse.json();
  return cart.attributes || {};
}

async function syncWayfinderOrderAttributes() {
  if (syncInFlight) {
    return;
  }

  syncInFlight = true;

  try {
    const attributes = await readCartAttributes();
    if (!attributes) {
      return;
    }

    if (isRentalStockupSelected(attributes)) {
      hideLocationHelp();
      return;
    }

    const copyPasteMapLink = buildCopyPasteMapLink(attributes);
    const suggestedCheckoutAddress = buildSuggestedCheckoutAddress(attributes);
    const nextAttributes = { ...attributes };

    if (copyPasteMapLink && attributes[COPY_PASTE_MAP_ATTRIBUTE] !== copyPasteMapLink) {
      nextAttributes[COPY_PASTE_MAP_ATTRIBUTE] = copyPasteMapLink;
    }

    if (suggestedCheckoutAddress && attributes[SUGGESTED_CHECKOUT_ADDRESS_ATTRIBUTE] !== suggestedCheckoutAddress) {
      nextAttributes[SUGGESTED_CHECKOUT_ADDRESS_ATTRIBUTE] = suggestedCheckoutAddress;
    }

    const hasUpdates =
      nextAttributes[COPY_PASTE_MAP_ATTRIBUTE] !== attributes[COPY_PASTE_MAP_ATTRIBUTE] ||
      nextAttributes[SUGGESTED_CHECKOUT_ADDRESS_ATTRIBUTE] !== attributes[SUGGESTED_CHECKOUT_ADDRESS_ATTRIBUTE];

    if (!hasUpdates) {
      if (hasSavedCoordinates(attributes)) {
        hideLocationHelp();
      }
      return;
    }

    if (lastSyncedMapLink === `${copyPasteMapLink}|${suggestedCheckoutAddress}`) {
      return;
    }

    lastSyncedMapLink = `${copyPasteMapLink}|${suggestedCheckoutAddress}`;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: nextAttributes,
      }),
    });

    hideLocationHelp();
  } finally {
    syncInFlight = false;
  }
}

function installWayfinderLocationWatcher() {
  const wayfinderContainer = document.querySelector('[data-wakestop-wayfinder-container]');
  if (!wayfinderContainer || wayfinderContainer.dataset.wakestopLocationWatcher) {
    return;
  }

  wayfinderContainer.dataset.wakestopLocationWatcher = 'true';

  wayfinderContainer.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const clickedLocationButton = target.closest(
      'button, [role="button"], input[type="button"], input[type="submit"]'
    );

    if (!clickedLocationButton) {
      return;
    }

    const label = clickedLocationButton.textContent?.trim().toLowerCase() || '';
    if (!label.includes('location') && !label.includes('my location')) {
      return;
    }

    window.setTimeout(async () => {
      const attributes = await readCartAttributes();
      if (!attributes) {
        return;
      }

      if (isRentalStockupSelected(attributes)) {
        hideLocationHelp();
        return;
      }

      if (hasSavedCoordinates(attributes)) {
        hideLocationHelp();
        return;
      }

      showLocationHelp(
        'We could not save your GPS pin yet. Allow location access for this site in your browser settings, then tap Use My Location again or drop the pin on the map.'
      );
    }, 2500);
  });
}

function installJqueryCartUpdateShim() {
  const jq = window.jQuery || window.$;
  if (!jq || jq.__wakestopCartJsonShim) {
    return Boolean(jq && jq.__wakestopCartJsonShim);
  }

  jq.__wakestopCartJsonShim = true;
  jq.ajaxPrefilter((options) => {
    if (options.url && String(options.url).includes('/cart/update.js')) {
      options.dataType = 'json';
    }
  });

  jq(document).ajaxComplete((_event, _xhr, settings) => {
    if (settings.url && String(settings.url).includes('/cart/update.js')) {
      window.setTimeout(syncWayfinderOrderAttributes, 300);
    }
  });

  return true;
}

function bootstrapWayfinderHelpers() {
  installWayfinderLocationWatcher();
  installJqueryCartUpdateShim();
  syncWayfinderOrderAttributes();
}

document.addEventListener('change', (event) => {
  if (event.target?.matches?.(`input[name="attributes[${FULFILLMENT_INTENT_ATTRIBUTE}]"]`) && isRentalStockupSelected()) {
    hideLocationHelp();
  }
});

const originalFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const response = await originalFetch(...args);
  const requestUrl = String(args[0]?.url || args[0] || '');

  if (requestUrl.includes('/cart/update.js')) {
    window.setTimeout(syncWayfinderOrderAttributes, 500);
  }

  return response;
};

bootstrapWayfinderHelpers();
window.setTimeout(bootstrapWayfinderHelpers, 1500);
window.setTimeout(syncWayfinderOrderAttributes, 4000);
window.addEventListener('focus', syncWayfinderOrderAttributes);
document.addEventListener('cart:update', () => {
  window.setTimeout(syncWayfinderOrderAttributes, 500);
});

// Cart checkout gate awaits this before sending customers to Shopify checkout.
window.wakestopSyncWayfinderAttributes = syncWayfinderOrderAttributes;
